import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWebSocket, WebSocketNotification } from './useWebSocket';

/**
 * Real-time data synchronization hook
 *
 * Connects WebSocket notifications to React Query cache invalidation
 * to ensure UI stays in sync with server data.
 *
 * @example
 * ```tsx
 * // In your main layout or App component
 * const { connectionStatus } = useRealtimeSync();
 * ```
 */
export const useRealtimeSync = () => {
  const queryClient = useQueryClient();

  /**
   * Handle incoming notifications and invalidate relevant queries
   */
  const handleNotification = useCallback((notification: WebSocketNotification) => {
    switch (notification.type) {
      // Sample Request related events
      case 'NEW_SAMPLE_REQUEST':
        queryClient.invalidateQueries({ queryKey: ['sampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['adminSampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['adminAllProjects'] });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['myRequests'] });
        queryClient.invalidateQueries({ queryKey: ['statistics'] });
        break;

      case 'SAMPLE_REQUEST_STATUS_CHANGE':
        queryClient.invalidateQueries({ queryKey: ['sampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['adminSampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['myRequests'] });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['adminAllProjects'] });
        // Also invalidate specific request if ID is available
        if (notification.data?.requestId) {
          queryClient.invalidateQueries({ queryKey: ['sampleRequest', notification.data.requestId] });
          queryClient.invalidateQueries({ queryKey: ['adminSampleRequest', notification.data.requestId] });
        }
        break;

      case 'SAMPLE_REQUEST_UPDATED':
        queryClient.invalidateQueries({ queryKey: ['sampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['adminSampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['myRequests'] });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['adminAllProjects'] });
        if (notification.data?.requestId) {
          queryClient.invalidateQueries({ queryKey: ['sampleRequest', notification.data.requestId] });
          queryClient.invalidateQueries({ queryKey: ['adminSampleRequest', notification.data.requestId] });
        }
        break;

      // Feedback related events
      case 'NEW_FEEDBACK':
      case 'FEEDBACK_STATUS_CHANGE':
      case 'NEW_FEEDBACK_REPLY':
        queryClient.invalidateQueries({ queryKey: ['feedbacks'] });
        queryClient.invalidateQueries({ queryKey: ['myFeedbacks'] });
        if (notification.data?.feedbackId) {
          queryClient.invalidateQueries({ queryKey: ['feedback', notification.data.feedbackId] });
        }
        break;

      // Project related events
      case 'PROJECT_UPDATE':
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['adminAllProjects'] });
        queryClient.invalidateQueries({ queryKey: ['adminSampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['myRequests'] });
        if (notification.data?.projectId) {
          queryClient.invalidateQueries({ queryKey: ['project', notification.data.projectId] });
        }
        break;

      // Quotation related events
      case 'QUOTATION_UPDATE':
      case 'NEW_QUOTATION':
        queryClient.invalidateQueries({ queryKey: ['quotations'] });
        break;

      // Brand related events
      case 'BRAND_UPDATE':
        queryClient.invalidateQueries({ queryKey: ['adminBrands'] });
        queryClient.invalidateQueries({ queryKey: ['brandInfo'] });
        break;

      // Manager related events
      case 'MANAGER_UPDATE':
        queryClient.invalidateQueries({ queryKey: ['managers'] });
        break;

      // General refresh - invalidate common queries
      case 'DATA_REFRESH':
        queryClient.invalidateQueries({ queryKey: ['sampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['adminSampleRequests'] });
        queryClient.invalidateQueries({ queryKey: ['myRequests'] });
        queryClient.invalidateQueries({ queryKey: ['projects'] });
        queryClient.invalidateQueries({ queryKey: ['adminAllProjects'] });
        break;

      default:
        // For unknown notification types, no action needed
        break;
    }
  }, [queryClient]);

  const {
    connectionStatus,
    notifications,
    unreadCount,
    connect,
    disconnect,
    markAsRead,
    clearNotifications,
  } = useWebSocket({
    onNotification: handleNotification,
    showToast: false,
    autoReconnect: true,
  });

  return {
    connectionStatus,
    notifications,
    unreadCount,
    connect,
    disconnect,
    markAsRead,
    clearNotifications,
  };
};

export default useRealtimeSync;
